import { useMemo } from 'react';

import { hasColumns, hasDomain, hasService } from '@/shared/lib/lineage/filters';
import { transformToReactFlowEdges, transformToReactFlowNodes } from '@/shared/lib/lineage/transform';
import type { LineageFilterState } from '@/shared/types/lineage';

import type { LineageResponse } from '@/features/asset-detail/api/lineageApi';

export const useLineageData = (lineageData: LineageResponse | undefined, filterState: LineageFilterState) => {
  const data = useMemo(() => {
    try {
      return lineageData || { nodes: {}, upstreamEdges: {}, downstreamEdges: {} };
    } catch (error) {
      console.error('Failed to process lineage data:', error);
      return { nodes: {}, upstreamEdges: {}, downstreamEdges: {} };
    }
  }, [lineageData]);

  const availableHierarchies = useMemo(() => {
    const defaultHierarchies = {
      hasService: false,
      hasDomain: false,
      hasColumns: false,
    };

    if (!data?.nodes) {
      return defaultHierarchies;
    }

    try {
      return {
        hasService: hasService(data.nodes),
        hasDomain: hasDomain(data.nodes),
        hasColumns: hasColumns(data.nodes),
      };
    } catch (error) {
      console.error('Failed to calculate hierarchies:', error);
      return defaultHierarchies;
    }
  }, [data]);

  const initialNodes = useMemo(() => {
    if (!data?.nodes) {
      return [];
    }
    return transformToReactFlowNodes(data, filterState);
  }, [data, filterState]);

  const visibleNodeIds = useMemo(() => new Set(initialNodes.map((node) => node.id)), [initialNodes]);

  const showColumnLevel = useMemo(
    () => filterState.viewOptions.selectedHierarchies.has('columns'),
    [filterState.viewOptions.selectedHierarchies]
  );

  const initialEdges = useMemo(() => {
    if (!data?.nodes) {
      return [];
    }
    return transformToReactFlowEdges(data, visibleNodeIds, showColumnLevel);
  }, [data, visibleNodeIds, showColumnLevel]);

  return {
    data,
    availableHierarchies,
    initialNodes,
    initialEdges,
  };
};
